import { useTranslation } from 'react-i18next'
import { FC, useEffect, useState } from 'react'
import { Paper, Typography, Link, Button, Tooltip } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Add, ArrowLeft, Download, FileCopy, KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material'
import { translateGradeLevel } from 'utils/helpers'
import { useGET_curriculumMapQuery } from 'redux/api/unitsApi'
import MainTableActionButtons from 'components/organisms/MainTableActionButtons'
import { actionIcon, filesIcon } from 'utils/customizedTheme'
import DialogPopup from 'components/molecules/DialogPopup'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'redux/store'
import { setUnitBackUrl } from 'redux/ui/curriculumUI'
import { setLessonPlanBackUrl } from 'redux/ui/lessonPlansUI'

const CurriculumUnitsPage: FC = () => {
  const { t } = useTranslation()  
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()

  const subject_id = searchParams.get('subject_id')
  const grade_level = searchParams.get('grade_level')

  var seconds = new Date().getTime() / 1000;
  const backUrl=`/units?subject_id=${subject_id}&grade_level=${grade_level}&t=${seconds}`

  useEffect(() => {
    dispatch(setUnitBackUrl(backUrl))
    dispatch(setLessonPlanBackUrl(backUrl))
  }, [backUrl])

  const [selectedUnit, setSelectedUnit] = useState(null)
  const [hiddenCourses, setHiddenCourses] = useState([])
  const [hiddenAcademicPeriods, setHiddenAcademicPeriods] = useState([])
  const [hiddenUnits, setHiddenUnits] = useState([])


  const { data: data } = useGET_curriculumMapQuery({subject_id: subject_id, grade_level: grade_level}, 
    {skip: !subject_id || !grade_level})
  
  const user = useSelector((state: RootState) => state['user'].user)
  
  return (
    <>
        <Paper sx={{ margin: '0rem 1rem', marginBottom: '2rem', background: 'none' }} elevation={0}>
          <Link onClick={() => navigate(user.role && !['parent', 'student'].includes(user.role) ? '/curriculum-map' : '/my-curriculum-map')} className="setup-link">
            <Typography sx={{ display: 'flex', alignItems: 'center', fontWeight: '400', fontSize: '1.5rem' }}>
              <ArrowLeft sx={{ fontSize: '2rem' }} />
              {t('common.back-to-all-courses')}
            </Typography>
          </Link>
        </Paper>   

        {data &&
        <Paper sx={{ margin: '0rem 1rem', background: 'none' }} elevation={0}>
          {data.courses.map(c =>
            <div key={`course-${c.id}`} style={{ paddingLeft: '20px', marginBottom: '40px' }}>
              <div className="curriculum-map-line" style={{ background: 'none' }} onClick={() => {
                if(hiddenCourses.includes(c.id))
                  setHiddenCourses([...hiddenCourses.filter(hc => hc !== c.id)])
                else
                  setHiddenCourses([...hiddenCourses, c.id])
              }}>
                <div className="curriculum-map-line-arrow">{hiddenCourses.includes(c.id) ?  <KeyboardArrowRight /> : <KeyboardArrowDown />}</div>
                <div className="curriculum-map-line-title" style={{ color: '#000', left: '-15px', fontSize: '18px', fontWeight: 500 }}>
                  <span style={{ textTransform: 'uppercase' }}>{c.title}</span>, {`${t('common.grade')} ${translateGradeLevel(grade_level)}`}</div>
              
                {user.role && !['parent', 'student'].includes(user.role) &&
                <div className="curriculum-map-buttons">
                  <Button variant='contained' color='blue' size='small' 
                    onClick={() => navigate(`/units/add?course_id=${c.id}&course_title=${translateGradeLevel(grade_level,true)}. ${c.title}&grade_level=${grade_level}`) }><Add /> {t('common.add-unit')}</Button>
                  <Button variant='contained' color='primary' size='small'
                     onClick={() => navigate(`/lesson-plans/add`) } sx={{ marginLeft: '10px' }}><Add /> {t('common.add-lesson-plan')}</Button>
                </div>}
              </div>

              <div style={{ display: hiddenCourses.includes(c.id) ? 'none' : 'block'}}>
                {c.academic_periods.filter(p => p.units.length > 0).map(p => 
                  <div key={`academic-period-${p.id}`} style={{ paddingLeft: '20px' }}>
                    <div className="curriculum-map-line" style={{ background: '#42A5F5' }}  onClick={() => {
                        if(hiddenAcademicPeriods.includes(p.id))
                          setHiddenAcademicPeriods([...hiddenAcademicPeriods.filter(hp => hp !== p.id)])
                        else
                          setHiddenAcademicPeriods([...hiddenAcademicPeriods, p.id])
                      }}>
                      <div className="curriculum-map-line-arrow">{hiddenAcademicPeriods.includes(p.id) ?  <KeyboardArrowRight /> : <KeyboardArrowDown />}</div>
                      <div className="curriculum-map-line-title" style={{ textTransform: 'uppercase', color: '#fff', fontWeight: 600 }}>{p.title}</div>
                    </div>
                    <div style={{ display: hiddenAcademicPeriods.includes(p.id) ? 'none' : 'block'}}>
                      {p.units.map(u => 
                        <div key={`unit-${u.id}`} style={{ paddingLeft: '20px' }}>
                          <div className="curriculum-map-line" style={{ background: '#90caf9' }} onClick={() => {
                              if(hiddenUnits.includes(u.id))
                                setHiddenUnits([...hiddenUnits.filter(hu => hu !== u.id)])
                              else
                                setHiddenUnits([...hiddenUnits, u.id])
                            }}>
                            <div className="curriculum-map-line-arrow">{hiddenUnits.includes(u.id) ?  <KeyboardArrowRight /> : <KeyboardArrowDown />}</div>
                            <div className="curriculum-map-line-title" style={{ fontWeight: 600 }}>{t('setup-syllabus-main.unit')} {u.number}: {u.title}</div>

                            <div className="curriculum-map-buttons"  onClick={(e) => {
                                        e.stopPropagation()
                                      }}>
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                { u.materials.length >0 &&
                                <Tooltip title={`${t('common.view-resources')}`} arrow placement='top'>
                                  <Button
                                    onClick={(e) => {
                                        setSelectedUnit(u)
                                    }}
                                    variant='outlined'
                                    color={'black'}
                                    disableElevation
                                    sx={{ ...actionIcon, ...filesIcon }}
                                  >
                                    <FileCopy />
                                  </Button>
                                </Tooltip>
                                }
                                <MainTableActionButtons row={{
                                    ...u, 
                                    actions: {
                                      is_viewable: user.role && !['parent', 'student'].includes(user.role),
                                      is_editable: user.role && !['parent', 'student'].includes(user.role),
                                      is_deletable: user.role && !['parent', 'student'].includes(user.role),
                                    }
                                  }} baseUrl={'/units/'} color='black' />
                                </div>
                            </div>
                          </div>
                          <div style={{ display: hiddenUnits.includes(u.id) ? 'none' : 'block'}}>
                              {u.lessons.map(l =>
                                <div key={`lesson-${l.id}`} style={{ paddingLeft: '20px' }}>
                                  <div className="curriculum-map-line">
                                    <div className="curriculum-map-line-title">{t('common.lesson')} {l.number}: {l.title}</div>
                                    <div className="curriculum-map-buttons">
                                      <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <MainTableActionButtons row={{
                                          ...l, 
                                          actions: {
                                            is_viewable: true,
                                            is_editable: user.role && !['parent', 'student'].includes(user.role),
                                            is_deletable: user.role && !['parent', 'student'].includes(user.role),
                                          }
                                        }} baseUrl={'/lesson-plans/'} color='black' />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}  
        </Paper>
        }

      {selectedUnit && <DialogPopup
          title={`${t('setup-syllabus-main.unit')} ${selectedUnit.number}: ${selectedUnit.title} / ${t('common.view-resources')}`}
          open={true}
          onClose={() => setSelectedUnit(null)}
          popupWidth={'1000px'}
        >
          {selectedUnit.materials.map(m => <div key={m.id} style={{ marginBottom: '10px'}}>{
            <Link onClick={() => {
              window.open(m.url, '_blank', 'rel=noopener noreferrer')
            }} style={{ 
              fontSize: '20px', 
              color: '#333',
              display: 'flex', 
              alignItems: 'center',
              gap: '5px',
              cursor: 'pointer'
            }}><Download /> {m.title}</Link>
          }</div>)}
        </DialogPopup>
        }

    </>
  )
}
export default CurriculumUnitsPage
